import type { Edge, Node } from '../../../core/models/scene';
import { routeEdges } from '../../../core/routing/routeEdges';
import type {
  EdgeRequest,
  Obstacle,
  Rect as RouterRect,
  Side,
} from '../../../core/routing/types';

const FLOW_EDGE_TYPES = new Set([
  'sequence_flow',
  'message_flow',
  'exception_flow',
  'data_association',
]);

const DEFAULT_LAYOUT_SOURCE = 'process-swimlane';

export interface ProcessEdgeRouteUpdate {
  fromAnchor: Side;
  toAnchor: Side;
  fromOffset: number;
  toOffset: number;
  attrs: Record<string, unknown>;
  failed?: boolean;
}

export interface ProcessEdgeRoutingOptions {
  layoutSource?: string;
  clearance?: number;
  visualClearance?: number;
  endpointStubLength?: number;
  turnPenalty?: number;
  laneSpacing?: number;
  debug?: boolean;
}

/**
 * Obstacle-aware routing pass for generated process/swimlane maps.
 *
 * ELK/swimlane layout owns node placement. This service owns final
 * connector geometry: it treats process shapes and lane headers as
 * blockers, routes flow edges through the shared orthogonal router,
 * and stamps the routed polyline in the same format Compass uses.
 */
export class ProcessEdgeRoutingService {
  route(
    nodes: Node[],
    edges: Edge[],
    options: ProcessEdgeRoutingOptions = {},
  ): Map<string, ProcessEdgeRouteUpdate> {
    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    const obstacles = buildObstacles(nodes);
    const obstacleIds = new Set(obstacles.map((o) => o.id));

    const requests: EdgeRequest[] = [];
    for (const edge of edges) {
      if (!isRoutableFlowEdge(edge)) continue;
      if (!obstacleIds.has(edge.from) || !obstacleIds.has(edge.to)) continue;
      requests.push({
        id: edge.id,
        fromObstacleId: edge.from,
        toObstacleId: edge.to,
        fromSide: asSide(edge.fromAnchor),
        toSide: asSide(edge.toAnchor),
      });
    }

    if (requests.length === 0) return new Map();

    const routed = routeEdges(obstacles, requests, {
      clearance: options.clearance ?? 24,
      visualClearance: options.visualClearance ?? options.clearance ?? 24,
      endpointStubLength: options.endpointStubLength ?? 16,
      turnPenalty: options.turnPenalty ?? 48,
      laneSpacing: options.laneSpacing ?? 16,
      nudgerObstacleClearance: options.visualClearance ?? options.clearance ?? 24,
      dropOnFailure: false,
      debug: options.debug,
    });

    const layoutSource = options.layoutSource ?? DEFAULT_LAYOUT_SOURCE;
    const updates = new Map<string, ProcessEdgeRouteUpdate>();
    for (const request of requests) {
      const route = routed.get(request.id);
      const edge = edges.find((candidate) => candidate.id === request.id);
      if (!edge) continue;
      const fromNode = nodeById.get(edge.from);
      const toNode = nodeById.get(edge.to);
      if (!fromNode || !toNode) continue;

      if (!route || route.failed || route.points.length < 4) {
        updates.set(edge.id, {
          fromAnchor: asSide(edge.fromAnchor) ?? 'right',
          toAnchor: asSide(edge.toAnchor) ?? 'left',
          fromOffset: clampOffset(edge.fromOffset),
          toOffset: clampOffset(edge.toOffset),
          attrs: {
            layoutPoints: [],
            layoutSource,
            routingType: 'orthogonal',
            routeFailed: true,
          },
          failed: true,
        });
        continue;
      }

      const fromRect = rectFromNode(fromNode);
      const toRect = rectFromNode(toNode);
      const fromPoint = { x: route.points[0], y: route.points[1] };
      const toPoint = {
        x: route.points[route.points.length - 2],
        y: route.points[route.points.length - 1],
      };

      updates.set(edge.id, {
        fromAnchor: route.fromSide,
        toAnchor: route.toSide,
        fromOffset: offsetForPoint(fromRect, route.fromSide, fromPoint),
        toOffset: offsetForPoint(toRect, route.toSide, toPoint),
        attrs: {
          layoutPoints: route.points,
          layoutSource,
          routingType: 'orthogonal',
          routeFailed: false,
        },
      });
    }

    return updates;
  }
}

export function applyProcessEdgeRouteUpdates(
  edges: Edge[],
  updates: Map<string, ProcessEdgeRouteUpdate>,
): void {
  for (const edge of edges) {
    const update = updates.get(edge.id);
    if (!update) continue;
    edge.fromAnchor = update.fromAnchor;
    edge.toAnchor = update.toAnchor;
    edge.fromOffset = update.fromOffset;
    edge.toOffset = update.toOffset;
    edge.attrs = {
      ...(edge.attrs ?? {}),
      ...update.attrs,
    };
  }
}

export function isRoutableProcessEdge(edge: Edge): boolean {
  return isRoutableFlowEdge(edge);
}

function buildObstacles(nodes: Node[]): Obstacle[] {
  const obstacles: Obstacle[] = [];
  for (const node of nodes) {
    if (isPoolTable(node)) continue;
    obstacles.push({ id: node.id, rect: rectFromNode(node) });
  }
  for (const pool of nodes.filter(isPoolTable)) {
    const header = laneHeaderRect(pool);
    if (header) {
      obstacles.push({
        id: `${pool.id}:lane-header`,
        rect: header,
      });
    }
  }
  return obstacles;
}

function isRoutableFlowEdge(edge: Edge): boolean {
  return FLOW_EDGE_TYPES.has(edge.type as string) && !!edge.from && !!edge.to;
}

function isPoolTable(node: Node): boolean {
  const attrs = node.attrs ?? {};
  const schemaId = typeof attrs.schemaId === 'string' ? attrs.schemaId : undefined;
  return attrs.__isPoolTable === true || schemaId === 'swimlane-horizontal' || schemaId === 'swimlane-vertical';
}

function laneHeaderRect(pool: Node): RouterRect | null {
  const attrs = pool.attrs ?? {};
  const direction =
    attrs.laneDirection === 'vertical' || attrs.schemaId === 'swimlane-vertical'
      ? 'vertical'
      : 'horizontal';
  const rect = rectFromNode(pool);
  if (rect.width <= 0 || rect.height <= 0) return null;

  if (direction === 'horizontal') {
    const headerWidth = firstNumber(attrs.columnWidths) ?? 50;
    return {
      x: rect.x,
      y: rect.y,
      width: Math.min(headerWidth, rect.width),
      height: rect.height,
    };
  }

  const headerHeight = firstNumber(attrs.rowHeights) ?? 50;
  return {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: Math.min(headerHeight, rect.height),
  };
}

function rectFromNode(node: Node): RouterRect {
  const width = node.w ?? node.width ?? 120;
  const height = node.h ?? node.height ?? 60;
  return {
    x: node.x ?? 0,
    y: node.y ?? 0,
    width,
    height,
  };
}

function offsetForPoint(rect: RouterRect, side: Side, point: { x: number; y: number }): number {
  if (side === 'top' || side === 'bottom') {
    return clampOffset((point.x - rect.x) / Math.max(1, rect.width));
  }
  return clampOffset((point.y - rect.y) / Math.max(1, rect.height));
}

function clampOffset(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0.5;
  return Math.max(0.05, Math.min(0.95, n));
}

function asSide(value: unknown): Side | undefined {
  return value === 'top' || value === 'right' || value === 'bottom' || value === 'left'
    ? value
    : undefined;
}

function firstNumber(value: unknown): number | undefined {
  return Array.isArray(value) && typeof value[0] === 'number' ? value[0] : undefined;
}
